/**
 * New evaluation dataset. Template example: NextAdmin `profile/account/page.tsx` (`FormPage` + `FormSection` +
 * `FormActions`), demo `/forms/form-layout`.
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  createEvaluationDatasetMutation,
  listEvaluationDatasetsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zEvaluationDatasetCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { useToast } from '../../components/ui/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { TARGET_TYPE_OPTIONS } from './evaluation-dataset-options.js';

const LIST_PATH = '/admin/evaluations';

export const NewEvaluationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createEvaluationDatasetMutation());
  const form = useZodForm(zEvaluationDatasetCreate, {
    defaultValues: { name: '', description: '', targetType: 'agent_template', targetId: '' },
  });

  return (
    <Can policy="RequireSuperadmin">
      <FormPage
        title="Create evaluation dataset"
        subtitle="A set of test cases that measures the quality of an agent, workflow or tool."
        breadcrumbs={[
          { label: 'Libraries', to: '/agents/libraries' },
          { label: 'Evaluations', to: LIST_PATH },
          { label: 'New' },
        ]}
        backTo={LIST_PATH}
        backLabel="Evaluations"
      >
        <Form
          form={form}
          onSubmit={async (body) => {
            await create.mutateAsync({ body: { ...body, description: body.description || undefined } });
            await queryClient.invalidateQueries({ queryKey: listEvaluationDatasetsQueryKey() });
            notify('Dataset created');
            navigate(LIST_PATH);
          }}
          className="space-y-8"
        >
          <FormSection title="Dataset">
            <TextFormField name="name" label="Name" placeholder="e.g. Risk manager accuracy" required />
            <SelectFormField name="targetType" label="Target type" options={TARGET_TYPE_OPTIONS} required />
            <TextFormField
              name="targetId"
              label="Target ID"
              placeholder="e.g. risk-manager"
              description="The slug, ID or name of what is evaluated"
              required
            />
            <FormFullWidth>
              <TextAreaFormField
                name="description"
                label="Description"
                rows={2}
                placeholder="What does this dataset test?"
              />
            </FormFullWidth>
          </FormSection>
          <FormActions submitLabel="Create dataset" onCancel={() => navigate(LIST_PATH)} />
        </Form>
      </FormPage>
    </Can>
  );
};

export default NewEvaluationPage;
